import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "button", "spinner"]
  static values = {
    url: String,
    pageId: String
  }

  connect() {
    console.log("AI Meta Description controller connected")
  }

  async generate(event) {
    event.preventDefault()

    // Get the page content from BlockNote editor
    const content = this.#getPageContent()

    if (!content) {
      alert("Please add some content to the page first")
      return
    }

    // Show loading state
    this.#setLoadingState(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#csrfToken()
        },
        body: JSON.stringify({
          content: content,
          page_id: this.pageIdValue
        })
      })

      const data = await response.json()

      if (response.ok) {
        // Update the textarea with the generated description
        this.textareaTarget.value = data.meta_description
        // Trigger change event so any other listeners are notified
        this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
      } else {
        alert(data.error || "Failed to generate meta description")
      }
    } catch (error) {
      console.error("Error generating meta description:", error)
      alert("An error occurred while generating the meta description")
    } finally {
      this.#setLoadingState(false)
    }
  }

  #getPageContent() {
    // Try to get content from the hidden field that stores BlockNote content
    const contentField = document.querySelector('input[name="page[content]"]')
    if (contentField && contentField.value) {
      try {
        const contentData = JSON.parse(contentField.value)
        return this.#extractTextFromBlockNote(contentData)
      } catch (e) {
        console.error("Failed to parse content:", e)
      }
    }

    // Fallback: try to get from BlockNote editor directly
    const editorElement = document.querySelector('[data-controller*="blocks-text-editor"]')
    if (editorElement) {
      // Get the content from the BlockNote editor
      const contentAttr = editorElement.dataset.blocksTextEditorContentValue
      if (contentAttr) {
        try {
          const contentData = JSON.parse(contentAttr)
          return this.#extractTextFromBlockNote(contentData)
        } catch (e) {
          console.error("Failed to parse editor content:", e)
        }
      }
    }

    return null
  }

  #extractTextFromBlockNote(contentData) {
    if (!contentData || !Array.isArray(contentData)) return ""

    let text = ""

    const extractFromBlock = (block) => {
      // Extract text from content array
      if (block.content && Array.isArray(block.content)) {
        block.content.forEach(item => {
          if (item.type === "text" && item.text) {
            text += item.text + " "
          }
        })
      }

      // Recursively extract from children
      if (block.children && Array.isArray(block.children)) {
        block.children.forEach(child => extractFromBlock(child))
      }
    }

    contentData.forEach(block => extractFromBlock(block))

    return text.trim()
  }

  #setLoadingState(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
    }

    if (this.hasSpinnerTarget) {
      if (loading) {
        this.spinnerTarget.classList.remove("hidden")
      } else {
        this.spinnerTarget.classList.add("hidden")
      }
    }

    // Update button text
    if (this.hasButtonTarget) {
      const buttonText = this.buttonTarget.querySelector('.button-text')
      if (buttonText) {
        buttonText.textContent = loading ? "Generating..." : "Generate with AI"
      }
    }
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
